import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { styled, useTheme } from "@mui/material/styles";

import {
  Add,
  Edit,
  Refresh,
  Close,
  Save,
  Search,
  FilterList,
} from "@mui/icons-material";

import {
  Alert,
  Box,
  Button,
  Card,
  Select,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { useBranch } from "./../../layouts/BranchContext";

/* =========================================================
   CUSTOM TABLE SCROLLBAR
   Used everywhere a scrollable area appears: the table
   container and all modal scroll areas.
========================================================= */

const CustomTableScrollbar = styled("div")(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    height: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* Scrollable modal bodies use the same scrollbar. */

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  "&::-webkit-scrollbar": {
    width: "6px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.2)"
        : "rgba(0,0,0,0.2)",
    borderRadius: "3px",
  },
  "&::-webkit-scrollbar-thumb:hover": {
    backgroundColor:
      theme.palette.mode === "dark"
        ? "rgba(255,255,255,0.3)"
        : "rgba(0,0,0,0.3)",
  },
  scrollbarWidth: "thin",
  scrollbarColor: `${
    theme.palette.mode === "dark"
      ? "rgba(255,255,255,0.2)"
      : "rgba(0,0,0,0.2)"
  } transparent`,
}));

/* =========================================================
   API
========================================================= */

const API_BASE_URL = "https://wims.revisetoexcel.com/api";

const ENDPOINTS = {
  list: "/hr/leave-applications/list/",
  history: "/hr/leave-applications/history/",
  create: "/hr/leave-applications/",
  detail: (id) => `/hr/leave-applications/${id}/`,
  update: (id) => `/hr/leave-applications/${id}/update/`,
  approve: (id) => `/hr/leave-applications/${id}/approve/`,
  reject: (id) => `/hr/leave-applications/${id}/reject/`,
  cancel: (id) => `/hr/leave-applications/${id}/cancel/`,
  leaveTypesDropdown: "/hr/leave-types/dropdown/",
  employeesFilter: "/staff/employees/filter/",
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

/* =========================================================
   ACCESS TOKEN
========================================================= */

const getAccessToken = () => {
  if (typeof window === "undefined") return null;

  const tokenKeys = ["accessToken", "access_token", "token"];

  for (const key of tokenKeys) {
    const localToken = localStorage.getItem(key);

    if (localToken) return localToken;

    const sessionToken = sessionStorage.getItem(key);

    if (sessionToken) return sessionToken;
  }

  return null;
};

/* =========================================================
   AXIOS INTERCEPTOR
========================================================= */

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* =========================================================
   HELPERS
========================================================= */

const getStoredUser = () => {
  if (typeof window === "undefined") return {};

  try {
    return JSON.parse(
      sessionStorage.getItem("user") ||
        localStorage.getItem("user") ||
        "{}"
    );
  } catch (error) {
    console.error("Unable to read logged-in user:", error);

    return {};
  }
};

const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "object") {
    return normalizeId(
      value.id ??
        value.pk ??
        value.value ??
        value.branch_id ??
        value.organization_id
    );
  }

  return String(value);
};

const normalizeArray = (data) => {
  if (Array.isArray(data)) return data;

  if (Array.isArray(data?.results)) return data.results;

  if (Array.isArray(data?.data)) return data.data;

  if (Array.isArray(data?.items)) return data.items;

  return [];
};

const getErrorMessage = (error) => {
  if (error?.response?.status === 401) {
    return "Unauthorized. Please login again.";
  }

  if (error?.response?.data?.detail) {
    return error.response.data.detail;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.response?.data) {
    const data = error.response.data;

    if (typeof data === "string") return data;

    return Object.values(data).flat().join(" ");
  }

  return error?.message || "Something went wrong.";
};

/* =========================================================
   COMPONENT
========================================================= */

function LeaveApplication() {
  const theme = useTheme();

  const { selectedBranchId } = useBranch();

  const currentUser = useMemo(() => getStoredUser(), []);

  const organizationId = normalizeId(
    currentUser?.organization_id ??
      currentUser?.organization?.id ??
      currentUser?.organization
  );

  const isDark = theme.palette.mode === "dark";
  const textPrimary = theme.palette.text.primary;
  const textSecondary = theme.palette.text.secondary;
  const textprimary = theme.palette.text.disabled;
  const borderColor = theme.palette.divider;
  const inputBackground = theme.palette.background.paper;

  /* =======================================================
     DATA
  ======================================================= */

  const [applications, setApplications] = useState([]);

  const [employees, setEmployees] = useState([]);

  const [leaveTypes, setLeaveTypes] = useState([]);

  /* =======================================================
     LOADING
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [saving, setSaving] = useState(false);

  /* =======================================================
     FILTER MODAL
  ======================================================= */

  const [filterModalOpen, setFilterModalOpen] = useState(false);

  /* =======================================================
     FILTERS
  =======================================================
     Branch comes from the global selector (useBranch).
     The list endpoint is fully server-side:
     organization, branch, employee, leave_type, status,
     from_date, to_date, year, search — all query params.
     Pagination stays client-side (plain array response).
  ======================================================= */

  /* View mode — "current" uses /list/, "history" uses
     /history/. Both endpoints accept the exact same query
     params and return the same EmployeeLeaveApplication
     array, so all filters/search/pagination work in
     either mode. */

  const [viewMode, setViewMode] = useState("current");

  const [filterStatus, setFilterStatus] = useState("");

  const [filterEmployee, setFilterEmployee] = useState("");

  const [filterLeaveType, setFilterLeaveType] = useState("");

  const [dateFrom, setDateFrom] = useState("");

  const [dateTo, setDateTo] = useState("");

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  /* Server-side search — sent as the `search` param,
     triggered on Enter or the search button. */

  const [searchInput, setSearchInput] = useState("");

  const [searchText, setSearchText] = useState("");

  /* =======================================================
     PAGINATION
  ======================================================= */

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(10);

  /* =======================================================
     CREATE / EDIT
  ======================================================= */

  const [openDialog, setOpenDialog] = useState(false);

  const [editMode, setEditMode] = useState(false);

  const [selectedApplicationId, setSelectedApplicationId] = useState(null);

  /* =======================================================
     FORM
  =======================================================
     Matches the Swagger create/update body:
     { employee, leave_type, from_date, to_date, reason,
       rejection_reason }
  ======================================================= */

  const [formData, setFormData] = useState({
    employee: "",
    leave_type: "",
    from_date: "",
    to_date: "",
    reason: "",
  });

  /* =======================================================
     EMPLOYEE MODAL
  ======================================================= */

  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);

  const [employeeSearch, setEmployeeSearch] = useState("");

  /* =======================================================
     ACTION CONFIRMATION
  ======================================================= */

  const [actionModalOpen, setActionModalOpen] = useState(false);

  const [selectedApplication, setSelectedApplication] = useState(null);

  const [actionType, setActionType] = useState("");

  const [actionSaving, setActionSaving] = useState(false);

  const [actionReason, setActionReason] = useState("");

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* =======================================================
     YEARS
  ======================================================= */

  const years = useMemo(() => {
    const currentYear = new Date().getFullYear();

    return Array.from({ length: 7 }, (_, index) => currentYear - 3 + index);
  }, []);

  /* =======================================================
     COMMON DIALOG STYLE
  ======================================================= */

  const dialogPaperStyles = {
    backgroundColor: theme.palette.background.paper,
    color: textPrimary,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: isDark
      ? "0 20px 50px rgba(0,0,0,0.45)"
      : "0 20px 50px rgba(0,0,0,0.12)",
  };

  /* =======================================================
     SELECT STYLE
  ======================================================= */

  const selectStyles = {
    borderRadius: 2,
    backgroundColor: inputBackground,
    color: textPrimary,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": {
      color: textPrimary,
    },
  };

  /* =======================================================
     MENU STYLE
  ======================================================= */

  const menuProps = {
    PaperProps: {
      sx: {
        backgroundColor: theme.palette.background.paper,
        color: textPrimary,
        border: `1px solid ${theme.palette.divider}`,
        "& .MuiMenuItem-root": {
          color: textPrimary,
          "&:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "&.Mui-selected": {
            backgroundColor: isDark
              ? "rgba(255,255,255,0.12)"
              : "rgba(0,0,0,0.08)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255,255,255,0.16)"
                : "rgba(0,0,0,0.12)",
            },
          },
        },
      },
    },
  };

  /* =======================================================
     TEXT FIELD STYLE
  ======================================================= */

  const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
      color: textPrimary,
      backgroundColor: theme.palette.background.paper,
      "& fieldset": {
        borderColor,
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiInputBase-input": {
      color: textPrimary,
      WebkitTextFillColor: textPrimary,
    },
    "& .MuiInputBase-input::placeholder": {
      color: textprimary,
      opacity: 0.8,
    },
  };

  /* =======================================================
     SNACKBAR
  ======================================================= */

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  /* =======================================================
     FETCH LEAVE APPLICATIONS
  =======================================================
     All filters are server-side query params on
     /hr/leave-applications/list/ — matching the Swagger
     exactly. Note the endpoint uses from_date / to_date
     (NOT date_from / date_to).
  ======================================================= */

  const fetchApplications = useCallback(
    async (overrides = {}) => {
      if (!organizationId) return;

      setLoading(true);

      try {
        const params = {
          organization: organizationId,
        };

        const normalizedBranch = normalizeId(selectedBranchId);

        if (normalizedBranch) {
          params.branch = normalizedBranch;
        }

        const status = overrides.status ?? filterStatus;

        const employee = overrides.employee ?? filterEmployee;

        const leaveType = overrides.leaveType ?? filterLeaveType;

        const year = overrides.year ?? selectedYear;

        const from = overrides.dateFrom ?? dateFrom;

        const to = overrides.dateTo ?? dateTo;

        const search = overrides.search ?? searchText;

        if (status !== "") {
          params.status = status;
        }

        if (employee !== "") {
          params.employee = normalizeId(employee);
        }

        if (leaveType !== "") {
          params.leave_type = normalizeId(leaveType);
        }

        if (year) {
          params.year = year;
        }

        if (from) {
          params.from_date = from;
        }

        if (to) {
          params.to_date = to;
        }

        if (search) {
          params.search = search;
        }

        const response = await api.get(
          viewMode === "history" ? ENDPOINTS.history : ENDPOINTS.list,
          { params }
        );

        setApplications(normalizeArray(response.data));
      } catch (error) {
        console.error("Leave Application List Error:", error);

        showSnackbar(getErrorMessage(error), "error");
      } finally {
        setLoading(false);
      }
    },
    [
      organizationId,
      selectedBranchId,
      filterStatus,
      filterEmployee,
      filterLeaveType,
      selectedYear,
      dateFrom,
      dateTo,
      searchText,
      viewMode,
    ]
  );

  /* =======================================================
     FETCH EMPLOYEES — scoped to global branch
  ======================================================= */

  const fetchEmployees = useCallback(async () => {
    if (!organizationId) return;

    try {
      const params = {
        organization_id: organizationId,
      };

      const normalizedBranch = normalizeId(selectedBranchId);

      if (normalizedBranch) {
        params.branch_id = normalizedBranch;
      }

      const response = await api.get(ENDPOINTS.employeesFilter, { params });

      setEmployees(normalizeArray(response.data));
    } catch (error) {
      console.error("Employee API Error:", error);

      setEmployees([]);

      showSnackbar(getErrorMessage(error), "error");
    }
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     FETCH LEAVE TYPES — scoped to global branch
  ======================================================= */

  const fetchLeaveTypes = useCallback(async () => {
    if (!organizationId) return;

    try {
      const params = {
        organization: organizationId,
      };

      const normalizedBranch = normalizeId(selectedBranchId);

      if (normalizedBranch) {
        params.branch = normalizedBranch;
      }

      const response = await api.get(ENDPOINTS.leaveTypesDropdown, { params });

      setLeaveTypes(normalizeArray(response.data));
    } catch (error) {
      console.error("Leave Type API Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    }
  }, [organizationId, selectedBranchId]);

  /* =======================================================
     INITIAL DATA
  ======================================================= */

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees]);

  useEffect(() => {
    fetchLeaveTypes();
  }, [fetchLeaveTypes]);

  /* =======================================================
     PAGINATION DATA
  ======================================================= */

  const displayedApplications = applications.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* =======================================================
     SEARCH — server-side, Enter / button triggered
  ======================================================= */

  const handleSearchSubmit = () => {
    setPage(0);

    setSearchText(searchInput.trim());

    fetchApplications({ search: searchInput.trim() });
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchText("");
    setPage(0);

    fetchApplications({ search: "" });
  };

  /* =======================================================
     OPEN CREATE
  ======================================================= */

  const handleOpenCreate = () => {
    setEditMode(false);

    setSelectedApplicationId(null);

    setFormData({
      employee: "",
      leave_type: "",
      from_date: "",
      to_date: "",
      reason: "",
    });

    setOpenDialog(true);
  };

  /* =======================================================
     OPEN EDIT — only reachable for pending rows;
     the update endpoint refuses non-pending rows anyway.
  ======================================================= */

  const handleOpenEdit = (application) => {
    setEditMode(true);

    setSelectedApplicationId(application.id);

    setFormData({
      employee:
        typeof application.employee === "object"
          ? application.employee?.id || ""
          : application.employee || "",
      leave_type:
        typeof application.leave_type === "object"
          ? application.leave_type?.id || ""
          : application.leave_type || "",
      from_date: application.from_date || "",
      to_date: application.to_date || "",
      reason: application.reason || "",
    });

    setOpenDialog(true);
  };

  /* =======================================================
     CLOSE FORM
  ======================================================= */

  const handleCloseDialog = () => {
    if (saving) return;

    setOpenDialog(false);

    setSelectedApplicationId(null);

    setEditMode(false);
  };

  /* =======================================================
     FORM CHANGE
  ======================================================= */

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =======================================================
     VALIDATION
  ======================================================= */

  const validateForm = () => {
    if (!formData.employee) {
      showSnackbar("Employee is required.", "error");

      return false;
    }

    if (!formData.leave_type) {
      showSnackbar("Leave type is required.", "error");

      return false;
    }

    if (!formData.from_date) {
      showSnackbar("From date is required.", "error");

      return false;
    }

    if (!formData.to_date) {
      showSnackbar("To date is required.", "error");

      return false;
    }

    if (new Date(formData.to_date) < new Date(formData.from_date)) {
      showSnackbar("To date cannot be before from date.", "error");

      return false;
    }

    if (!formData.reason.trim()) {
      showSnackbar("Reason is required.", "error");

      return false;
    }

    return true;
  };

  /* =======================================================
     CREATE
  =======================================================
     Payload matches the Swagger create body:
     { employee, leave_type, from_date, to_date, reason }
     rejection_reason is not sent on create — it belongs to
     the review flow (reject endpoint), not the application.
  ======================================================= */

  const handleCreate = async () => {
    if (!validateForm()) return;

    setSaving(true);

    try {
      const payload = {
        employee: Number(formData.employee),
        leave_type: Number(formData.leave_type),
        from_date: formData.from_date,
        to_date: formData.to_date,
        reason: formData.reason.trim(),
      };

      await api.post(ENDPOINTS.create, payload);

      showSnackbar("Leave application created successfully.", "success");

      setOpenDialog(false);

      setFormData({
        employee: "",
        leave_type: "",
        from_date: "",
        to_date: "",
        reason: "",
      });

      await fetchApplications();
    } catch (error) {
      console.error("Create Leave Application Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     UPDATE — same body as create, against PUT /{id}/update/
  ======================================================= */

  const handleUpdate = async () => {
    if (!validateForm()) return;

    if (!selectedApplicationId) return;

    setSaving(true);

    try {
      const payload = {
        employee: Number(formData.employee),
        leave_type: Number(formData.leave_type),
        from_date: formData.from_date,
        to_date: formData.to_date,
        reason: formData.reason.trim(),
      };

      await api.put(ENDPOINTS.update(selectedApplicationId), payload);

      showSnackbar("Leave application updated successfully.", "success");

      setOpenDialog(false);

      /* Refresh the edited row from GET /{id}/ so read-only
         fields (total_days, status, reviewed_by, ...) come
         from the API rather than client guesses. */

      try {
        const detailResponse = await api.get(
          ENDPOINTS.detail(selectedApplicationId)
        );

        const updated = detailResponse.data;

        setApplications((prev) =>
          prev.map((application) =>
            application.id === selectedApplicationId
              ? { ...application, ...updated }
              : application
          )
        );
      } catch (detailError) {
        console.error("Application detail refresh failed:", detailError);

        await fetchApplications();
      }
    } catch (error) {
      console.error("Update Leave Application Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     SAVE
  ======================================================= */

  const handleSave = () => {
    if (editMode) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  /* =======================================================
     EMPLOYEE SEARCH
  ======================================================= */

  const filteredEmployees = useMemo(() => {
    const search = employeeSearch.trim().toLowerCase();

    if (!search) return employees;

    return employees.filter((employee) => {
      const name =
        employee.name || employee.employee_name || employee.full_name || "";
      const code = employee.employee_code || "";
      const department = employee.department_name || "";
      const designation = employee.designation_name || "";
      const branch = employee.branch_name || "";

      return (
        name.toLowerCase().includes(search) ||
        code.toLowerCase().includes(search) ||
        department.toLowerCase().includes(search) ||
        designation.toLowerCase().includes(search) ||
        branch.toLowerCase().includes(search)
      );
    });
  }, [employees, employeeSearch]);

  const handleOpenEmployeeModal = () => {
    setEmployeeSearch("");
    setEmployeeModalOpen(true);
  };

  const handleCloseEmployeeModal = () => {
    setEmployeeModalOpen(false);
  };

  const handleSelectEmployee = (employee) => {
    setFormData((prev) => ({
      ...prev,
      employee: employee.id,
    }));

    setEmployeeModalOpen(false);
  };

  /* =======================================================
     DISPLAY HELPERS
  ======================================================= */

  const getEmployeeLabel = (employee) => {
    return (
      employee.name ||
      employee.employee_name ||
      employee.full_name ||
      employee.employee_code ||
      `Employee ${employee.id}`
    );
  };

  const getLeaveTypeLabel = (leaveType) => {
    return (
      leaveType.leave_name ||
      leaveType.name ||
      leaveType.leave_type_name ||
      leaveType.label ||
      `Leave Type ${leaveType.id}`
    );
  };

  const getApplicationEmployeeName = (application) => {
    return (
      application.employee_name ||
      application.employee?.name ||
      application.employee?.employee_name ||
      "-"
    );
  };

  const getApplicationEmployeeCode = (application) => {
    return (
      application.employee_code || application.employee?.employee_code || "-"
    );
  };

  const getApplicationLeaveType = (application) => {
    return (
      application.leave_type_name ||
      application.leave_type?.name ||
      application.leave_type?.leave_name ||
      "-"
    );
  };

  /* Status: prefer the API's own status_display field. */

  const getStatusLabel = (application) => {
    return application?.status_display || application?.status || "-";
  };

  const getStatusStyles = (status) => {
    const value = String(status || "").toLowerCase();

    if (value === "approved" || value === "approve") {
      return {
        backgroundColor: isDark ? "rgba(34,197,94,0.18)" : "#DCFCE7",
        color: isDark ? "#4ADE80" : "#15803D",
      };
    }

    if (value === "rejected" || value === "reject") {
      return {
        backgroundColor: isDark ? "rgba(239,68,68,0.18)" : "#FEE2E2",
        color: isDark ? "#F87171" : "#B91C1C",
      };
    }

    if (value === "cancelled" || value === "canceled" || value === "cancel") {
      return {
        backgroundColor: isDark ? "rgba(148,163,184,0.18)" : "#F1F5F9",
        color: isDark ? "#CBD5E1" : "#64748B",
      };
    }

    return {
      backgroundColor: isDark ? "rgba(251,191,36,0.18)" : "#FEF3C7",
      color: isDark ? "#FBBF24" : "#B45309",
    };
  };

  /* =======================================================
     ACTION CONFIRMATION
  ======================================================= */

  const handleOpenActionConfirmation = (application, action) => {
    setSelectedApplication(application);
    setActionType(action);
    setActionReason("");
    setActionModalOpen(true);
  };

  const handleCloseActionConfirmation = () => {
    if (actionSaving) return;

    setSelectedApplication(null);
    setActionType("");
    setActionReason("");
    setActionModalOpen(false);
  };

  /* =======================================================
     CONFIRM ACTION
  =======================================================
     approve / cancel: PATCH with no body.
     reject: PATCH with { rejection_reason } (required).
  ======================================================= */

  const handleConfirmAction = async () => {
    if (!selectedApplication || !actionType) return;

    if (actionType === "reject" && !actionReason.trim()) {
      showSnackbar("Rejection reason is required.", "error");

      return;
    }

    setActionSaving(true);

    try {
      const id = selectedApplication.id;

      if (actionType === "approve") {
        await api.patch(ENDPOINTS.approve(id));
      } else if (actionType === "reject") {
        await api.patch(ENDPOINTS.reject(id), {
          rejection_reason: actionReason.trim(),
        });
      } else if (actionType === "cancel") {
        await api.patch(ENDPOINTS.cancel(id));
      }

      const actionLabel =
        actionType.charAt(0).toUpperCase() + actionType.slice(1);

      showSnackbar(
        `Leave application ${actionLabel.toLowerCase()}${
          actionType === "approve" ? "d" : actionType === "cancel" ? "led" : "ed"
        } successfully.`,
        "success"
      );

      setActionModalOpen(false);
      setSelectedApplication(null);
      setActionType("");
      setActionReason("");

      await fetchApplications();
    } catch (error) {
      console.error("Leave Application Action Error:", error);

      showSnackbar(getErrorMessage(error), "error");
    } finally {
      setActionSaving(false);
    }
  };

  /* =======================================================
     FILTER APPLY / RESET
  ======================================================= */

  const handleApplyFilter = () => {
    setPage(0);

    setFilterModalOpen(false);

    fetchApplications();
  };

  const handleResetFilter = () => {
    setFilterStatus("");
    setFilterEmployee("");
    setFilterLeaveType("");
    setDateFrom("");
    setDateTo("");
    setSearchInput("");
    setSearchText("");
    setSelectedYear(new Date().getFullYear());
    setPage(0);
    setFilterModalOpen(false);

    fetchApplications({
      status: "",
      employee: "",
      leaveType: "",
      dateFrom: "",
      dateTo: "",
      search: "",
      year: new Date().getFullYear(),
    });
  };

  /* =======================================================
     PAGINATION
  ======================================================= */

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));

    setPage(0);
  };

  /* =======================================================
     UI
  ======================================================= */

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        minHeight: "100vh",
        color: textPrimary,
      }}
    >
      {/* ===================================================
          PAGE HEADER
      =================================================== */}

      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "stretch", lg: "center" },
          gap: 1.5,
          mb: 3,
          flexWrap: { xs: "wrap", lg: "nowrap" },
          width: "100%",
        }}
      >
        {/* SEARCH — server-side `search` param, Enter/button
            triggered */}

        <TextField
          size="small"
          variant="outlined"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              handleSearchSubmit();
            }
          }}
          placeholder="Search employee name or code..."
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ color: theme.palette.primary.main }} />
              </InputAdornment>
            ),
            endAdornment: (
              searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    sx={{ color: textPrimary }}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            ),
          }}
          sx={{
            flex: { xs: "1 1 100%", sm: "1 1 340px", lg: "1 1 auto" },
            minWidth: { lg: 280 },
            maxWidth: { lg: 380 },
            "& .MuiOutlinedInput-root": {
              height: 50,
              borderRadius: "30px",
              backgroundColor: inputBackground,
              color: textPrimary,
              "& input": {
                color: textPrimary,
                WebkitTextFillColor: textPrimary,
              },
              "& input::placeholder": {
                color: textPrimary,
                opacity: 0.75,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark
                  ? "rgba(255,255,255,0.28)"
                  : "rgba(0,0,0,0.22)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderWidth: 2,
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />

        {/* ACTIONS */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
            flexShrink: 0,
            width: { xs: "100%", lg: "auto" },
            justifyContent: { xs: "flex-start", lg: "flex-end" },
            ml: { lg: "auto" },
          }}
        >
          {/* SEARCH BUTTON — triggers the server-side search */}

          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleSearchSubmit}
            disabled={loading}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Search
          </Button>

          {/* VIEW TOGGLE — Applications (/list/) vs
              History (/history/) */}

          <Box
            sx={{
              display: "flex",
              height: 50,
              borderRadius: "10px",
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
            }}
          >
            <Button
              variant={viewMode === "current" ? "contained" : "text"}
              onClick={() => {
                setViewMode("current");
                setPage(0);
              }}
              sx={{
                px: 2.5,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                boxShadow: "none",
                color: viewMode === "current" ? "#FFFFFF" : textPrimary,
                "&:hover": { boxShadow: "none" },
              }}
            >
              Applications
            </Button>

            <Button
              variant={viewMode === "history" ? "contained" : "text"}
              onClick={() => {
                setViewMode("history");
                setPage(0);
              }}
              sx={{
                px: 2.5,
                textTransform: "none",
                fontWeight: 600,
                whiteSpace: "nowrap",
                boxShadow: "none",
                color: viewMode === "history" ? "#FFFFFF" : textPrimary,
                "&:hover": { boxShadow: "none" },
              }}
            >
              History
            </Button>
          </Box>

          {/* REFRESH */}

          <Button
            variant="contained"
            startIcon={<Refresh />}
            onClick={() => fetchApplications()}
            disabled={loading}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Refresh
          </Button>

          {/* FILTER */}

          <Button
            variant="contained"
            startIcon={<FilterList />}
            onClick={() => setFilterModalOpen(true)}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Filters
          </Button>

          {/* ADD APPLICATION */}

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{
              height: 50,
              px: 2.5,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              whiteSpace: "nowrap",
              boxShadow: "none",
              flexShrink: 0,
              "&:hover": { boxShadow: "none" },
            }}
          >
            Add Application
          </Button>
        </Box>
      </Box>

      {/* ===================================================
          FILTER MODAL
      =================================================== */}

      <Dialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            color: textPrimary,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary }}>
              Filter Leave Applications
            </Typography>

            <IconButton
              size="small"
              onClick={() => setFilterModalOpen(false)}
              sx={{
                color: textPrimary,
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <StyledDialogContent
          sx={{
            px: 3,
            py: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* YEAR — server-side param */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                YEAR
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={selectedYear}
                  onChange={(event) => setSelectedYear(event.target.value)}
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* ORGANIZATION — read-only, from session */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                ORGANIZATION
              </Typography>

              <Box
                sx={{
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 2,
                  px: 2,
                  py: 1.4,
                  backgroundColor: theme.palette.action.hover,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{ color: textPrimary }}
                >
                  {currentUser?.organization_name || organizationId || "-"}
                </Typography>
              </Box>
            </Box>

            {/* EMPLOYEE — server-side param, scoped to the
                global branch via the employees fetch */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                EMPLOYEE
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={filterEmployee}
                  onChange={(event) => setFilterEmployee(event.target.value)}
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="">All Employees</MenuItem>

                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {getEmployeeLabel(employee)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* LEAVE TYPE — server-side param */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                LEAVE TYPE
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={filterLeaveType}
                  onChange={(event) => setFilterLeaveType(event.target.value)}
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="">All Leave Types</MenuItem>

                  {leaveTypes.map((leaveType) => (
                    <MenuItem key={leaveType.id} value={leaveType.id}>
                      {getLeaveTypeLabel(leaveType)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* STATUS — server-side param */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                STATUS
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  value={filterStatus}
                  onChange={(event) => setFilterStatus(event.target.value)}
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="">All Statuses</MenuItem>

                  <MenuItem value="pending">Pending</MenuItem>

                  <MenuItem value="approved">Approved</MenuItem>

                  <MenuItem value="rejected">Rejected</MenuItem>

                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* FROM DATE — server-side `from_date` param */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                FROM DATE
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* TO DATE — server-side `to_date` param */}

            <Box>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                TO DATE
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="date"
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          </Box>
        </StyledDialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="text"
            onClick={handleResetFilter}
            sx={{
              textTransform: "none",
              color: textPrimary,
            }}
          >
            Reset
          </Button>

          <Button
            variant="contained"
            onClick={handleApplyFilter}
            sx={{
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          TABLE
      =================================================== */}

      <Card
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}
      >
        <CardContent
          sx={{
            p: 0,
            "&:last-child": { pb: 0 },
          }}
        >
          {/* MAIN TABLE — scrollable via CustomTableScrollbar */}

          <CustomTableScrollbar
            sx={{
              overflowX: "auto",
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Table sx={{ minWidth: 1250 }}>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    "& .MuiTableCell-root": {
                      color: "#FFFFFF",
                      fontWeight: 700,
                      fontSize: "0.82rem",
                      borderBottom: "none",
                      py: 1.8,
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <TableCell>S.No.</TableCell>

                  <TableCell>Employee</TableCell>

                  <TableCell>Leave Type</TableCell>

                  <TableCell>Branch</TableCell>

                  <TableCell>From Date</TableCell>

                  <TableCell>To Date</TableCell>

                  <TableCell>Days</TableCell>

                  <TableCell>Reason</TableCell>

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={{ py: 7, borderBottom: "none" }}
                    >
                      <CircularProgress
                        size={32}
                        sx={{ color: theme.palette.primary.main }}
                      />

                      <Typography sx={{ mt: 1.5, color: textPrimary }}>
                        Loading leave applications...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : displayedApplications.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={10}
                      align="center"
                      sx={{ py: 8, borderBottom: "none" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.95rem",
                          color: textPrimary,
                        }}
                      >
                        No leave applications found
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ mt: 0.7, color: textPrimary }}
                      >
                        Create a leave application to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  displayedApplications.map((application, index) => {
                    const status = String(application.status || "").toLowerCase();

                    /* Approve/Reject/Cancel only exist for
                       pending applications; Edit likewise. */

                    const isPending = status === "pending";

                    return (
                      <TableRow
                        key={application.id}
                        hover
                        sx={{
                          transition: "background-color 0.2s ease",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                          "& .MuiTableCell-root": {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            py: 1.5,
                            fontSize: "0.82rem",
                            color: textPrimary,
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600 }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>

                        {/* EMPLOYEE */}

                        <TableCell>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: "0.83rem",
                                color: textPrimary,
                              }}
                            >
                              {getApplicationEmployeeName(application)}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: textprimary,
                                fontSize: "0.72rem",
                              }}
                            >
                              {getApplicationEmployeeCode(application)}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* LEAVE TYPE */}

                        <TableCell>
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                fontSize: "0.82rem",
                                color: textPrimary,
                              }}
                            >
                              {getApplicationLeaveType(application)}
                            </Typography>

                            {application.leave_code && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: textprimary,
                                  fontSize: "0.7rem",
                                }}
                              >
                                {application.leave_code}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>

                        {/* BRANCH — uses the API's branch_name */}

                        <TableCell>
                          <Typography
                            sx={{ fontWeight: 600, color: textPrimary }}
                          >
                            {application.branch_name || "-"}
                          </Typography>
                        </TableCell>

                        {/* FROM DATE */}

                        <TableCell>
                          <Typography
                            sx={{ fontWeight: 600, color: textPrimary }}
                          >
                            {application.from_date || "-"}
                          </Typography>
                        </TableCell>

                        {/* TO DATE */}

                        <TableCell>
                          <Typography
                            sx={{ fontWeight: 600, color: textPrimary }}
                          >
                            {application.to_date || "-"}
                          </Typography>
                        </TableCell>

                        {/* DAYS — total_days is $decimal from the API */}

                        <TableCell>
                          <Typography
                            sx={{ fontWeight: 700, color: textPrimary }}
                          >
                            {application.total_days ?? "-"}
                          </Typography>
                        </TableCell>

                        {/* REASON — shows rejection_reason on
                            rejected rows instead */}

                        <TableCell sx={{ maxWidth: 260 }}>
                          <Tooltip
                            title={
                              application.reason ||
                              application.rejection_reason ||
                              "-"
                            }
                          >
                            <Typography
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 240,
                                color: textPrimary,
                              }}
                            >
                              {status === "rejected"
                                ? application.rejection_reason ||
                                  application.reason ||
                                  "-"
                                : application.reason || "-"}
                            </Typography>
                          </Tooltip>
                        </TableCell>

                        {/* STATUS — uses status_display from the API */}

                        <TableCell>
                          <Chip
                            size="small"
                            label={getStatusLabel(application)}
                            sx={{
                              height: 26,
                              fontSize: "0.72rem",
                              fontWeight: 700,
                              borderRadius: "6px",
                              ...getStatusStyles(application.status),
                            }}
                          />
                        </TableCell>

                        {/* ACTIONS */}

                        <TableCell align="center">
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: 0.6,
                              flexWrap: "wrap",
                            }}
                          >
                            {isPending && (
                              <>
                                <Tooltip title="Approve">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      handleOpenActionConfirmation(
                                        application,
                                        "approve"
                                      )
                                    }
                                    sx={{
                                      minWidth: 0,
                                      px: 1,
                                      textTransform: "none",
                                      color: theme.palette.success.main,
                                      borderColor:
                                        theme.palette.success.main,
                                    }}
                                  >
                                    Approve
                                  </Button>
                                </Tooltip>

                                <Tooltip title="Reject">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      handleOpenActionConfirmation(
                                        application,
                                        "reject"
                                      )
                                    }
                                    sx={{
                                      minWidth: 0,
                                      px: 1,
                                      textTransform: "none",
                                      color: theme.palette.error.main,
                                      borderColor: theme.palette.error.main,
                                    }}
                                  >
                                    Reject
                                  </Button>
                                </Tooltip>

                                <Tooltip title="Cancel">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() =>
                                      handleOpenActionConfirmation(
                                        application,
                                        "cancel"
                                      )
                                    }
                                    sx={{
                                      minWidth: 0,
                                      px: 1,
                                      textTransform: "none",
                                      color: textPrimary,
                                      borderColor: theme.palette.divider,
                                    }}
                                   data-app-cancel="true">
                                    Cancel
                                  </Button>
                                </Tooltip>

                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenEdit(application)}
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      color: theme.palette.primary.main,
                                      backgroundColor: isDark
                                        ? `${theme.palette.primary.main}22`
                                        : `${theme.palette.primary.main}12`,
                                      "&:hover": {
                                        backgroundColor:
                                          theme.palette.primary.main,
                                        color: "#FFFFFF",
                                      },
                                    }}
                                  >
                                    <Edit fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}

                            {!isPending && (
                              <Tooltip
                                title={
                                  application.reviewed_by_name
                                    ? `Reviewed by ${application.reviewed_by_name}`
                                    : "Only pending applications can be modified"
                                }
                              >
                                <Typography
                                  variant="caption"
                                  sx={{ color: textprimary }}
                                >
                                  {application.reviewed_by_name
                                    ? `by ${application.reviewed_by_name}`
                                    : "—"}
                                </Typography>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CustomTableScrollbar>

          {/* PAGINATION — client-side, the list endpoint
              returns a plain array */}

          <TablePagination
            component="div"
            count={applications.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              color: textPrimary,
              "& .MuiTablePagination-selectIcon": {
                color: textPrimary,
              },
              "& .MuiTablePagination-actions button": {
                color: textPrimary,
              },
            }}
          />
        </CardContent>
      </Card>

      {/* ===================================================
          CREATE / EDIT MODAL
      =================================================== */}

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="h6" fontWeight={700} sx={{ color: textPrimary }}>
              {editMode ? "Edit Leave Application" : "Create Leave Application"}
            </Typography>

            <IconButton
              size="small"
              onClick={handleCloseDialog}
              disabled={saving}
              sx={{
                color: textPrimary,
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <StyledDialogContent
          sx={{
            px: 3,
            py: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {/* EMPLOYEE */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                EMPLOYEE
              </Typography>

              <Box
                onClick={handleOpenEmployeeModal}
                sx={{
                  minHeight: 40,
                  border: `1px solid ${borderColor}`,
                  borderRadius: 2,
                  backgroundColor: inputBackground,
                  display: "flex",
                  alignItems: "center",
                  px: 1.7,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.9rem",
                    color: textPrimary,
                    fontWeight: formData.employee ? 600 : 400,
                  }}
                >
                  {formData.employee
                    ? (() => {
                        const selectedEmployee = employees.find(
                          (employee) =>
                            Number(employee.id) === Number(formData.employee)
                        );

                        return selectedEmployee
                          ? `${getEmployeeLabel(selectedEmployee)}${
                              selectedEmployee.employee_code
                                ? ` (${selectedEmployee.employee_code})`
                                : ""
                            }`
                          : "Select Employee";
                      })()
                    : "Select Employee"}
                </Typography>
              </Box>
            </Box>

            {/* LEAVE TYPE */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                LEAVE TYPE
              </Typography>

              <FormControl fullWidth size="small">
                <Select
                  name="leave_type"
                  value={formData.leave_type}
                  onChange={handleFormChange}
                  displayEmpty
                  MenuProps={menuProps}
                  sx={selectStyles}
                >
                  <MenuItem value="">Select Leave Type</MenuItem>

                  {leaveTypes.map((leaveType) => (
                    <MenuItem key={leaveType.id} value={leaveType.id}>
                      {getLeaveTypeLabel(leaveType)}

                      {leaveType.leave_code && ` (${leaveType.leave_code})`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {/* FROM DATE */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                FROM DATE
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="date"
                name="from_date"
                value={formData.from_date}
                onChange={handleFormChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* TO DATE */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                TO DATE
              </Typography>

              <TextField
                fullWidth
                size="small"
                type="date"
                name="to_date"
                value={formData.to_date}
                onChange={handleFormChange}
                sx={textFieldStyles}
                InputLabelProps={{ shrink: true }}
              />
            </Box>

            {/* REASON — part of the Swagger create/update body */}

            <Box>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mb: 1,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  color: theme.palette.primary.main,
                }}
              >
                REASON
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={4}
                name="reason"
                value={formData.reason}
                onChange={handleFormChange}
                placeholder="Enter reason for leave..."
                sx={textFieldStyles}
              />
            </Box>
          </Box>
        </StyledDialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1.2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseDialog}
            disabled={saving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              color: textPrimary,
              borderColor: theme.palette.divider,
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            startIcon={
              saving ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <Save fontSize="small" />
              )
            }
            onClick={handleSave}
            disabled={saving}
            sx={{
              minWidth: 170,
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
            }}
          >
            {saving
              ? "Saving..."
              : editMode
              ? "Update Application"
              : "Create Application"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          EMPLOYEE SELECTION MODAL
      =================================================== */}

      <Dialog
        open={employeeModalOpen}
        onClose={handleCloseEmployeeModal}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 2.5,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>
                Select Employee
              </Typography>

              <Typography variant="body2" sx={{ mt: 0.4, color: textprimary }}>
                Showing employees from the selected branch
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={handleCloseEmployeeModal}
              sx={{
                color: textPrimary,
                border: `1px solid ${theme.palette.divider}`,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>

        <Divider />

        <StyledDialogContent
          sx={{
            p: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {/* SEARCH */}

          <TextField
            fullWidth
            size="small"
            value={employeeSearch}
            onChange={(event) => setEmployeeSearch(event.target.value)}
            placeholder="Search employee, code, department..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              ),
              endAdornment:
                employeeSearch && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setEmployeeSearch("")}
                      sx={{ color: textPrimary }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
            }}
            sx={{
              mb: 2.5,
              "& .MuiOutlinedInput-root": {
                height: 46,
                borderRadius: 2,
                color: textPrimary,
                backgroundColor: inputBackground,
                "& fieldset": {
                  borderColor,
                },
                "&:hover fieldset": {
                  borderColor: theme.palette.primary.main,
                },
                "&.Mui-focused fieldset": {
                  borderColor: theme.palette.primary.main,
                },
              },
              "& input": {
                color: textPrimary,
                WebkitTextFillColor: textPrimary,
              },
              "& input::placeholder": {
                color: textprimary,
                opacity: 0.8,
              },
            }}
          />

          {/* EMPLOYEE LIST — scrollable via CustomTableScrollbar */}

          <CustomTableScrollbar
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.2,
              maxHeight: 430,
              overflowY: "auto",
              pr: 0.5,
            }}
          >
            {filteredEmployees.length === 0 ? (
              <Box
                sx={{
                  py: 7,
                  textAlign: "center",
                  border: `1px dashed ${theme.palette.divider}`,
                  borderRadius: 2,
                }}
              >
                <Typography fontWeight={700} sx={{ color: textPrimary }}>
                  No employees found
                </Typography>

                <Typography variant="body2" sx={{ mt: 0.5, color: textprimary }}>
                  Try a different employee name or code.
                </Typography>
              </Box>
            ) : (
              filteredEmployees.map((employee) => {
                const selected =
                  Number(formData.employee) === Number(employee.id);

                return (
                  <Box
                    key={employee.id}
                    onClick={() => handleSelectEmployee(employee)}
                    sx={{
                      border: `1px solid ${
                        selected
                          ? theme.palette.primary.main
                          : theme.palette.divider
                      }`,
                      borderRadius: 2,
                      p: 1.8,
                      cursor: "pointer",
                      backgroundColor: selected
                        ? isDark
                          ? `${theme.palette.primary.main}18`
                          : `${theme.palette.primary.main}08`
                        : theme.palette.background.paper,
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: theme.palette.primary.main,
                        backgroundColor: theme.palette.action.hover,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: "0.9rem",
                              color: textPrimary,
                            }}
                          >
                            {getEmployeeLabel(employee)}
                          </Typography>

                          {employee.employee_code && (
                            <Chip
                              size="small"
                              label={employee.employee_code}
                              sx={{
                                height: 23,
                                fontSize: "0.68rem",
                                fontWeight: 700,
                                borderRadius: "5px",
                                backgroundColor: isDark
                                  ? "rgba(255,255,255,0.08)"
                                  : "rgba(0,0,0,0.05)",
                                color: textPrimary,
                              }}
                            />
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1.5,
                            mt: 1,
                          }}
                        >
                          {employee.department_name && (
                            <Typography
                              variant="caption"
                              sx={{ color: textprimary }}
                            >
                              <strong>Department:</strong>{" "}
                              {employee.department_name}
                            </Typography>
                          )}

                          {employee.designation_name && (
                            <Typography
                              variant="caption"
                              sx={{ color: textprimary }}
                            >
                              <strong>Designation:</strong>{" "}
                              {employee.designation_name}
                            </Typography>
                          )}

                          {employee.branch_name && (
                            <Typography
                              variant="caption"
                              sx={{ color: textprimary }}
                            >
                              <strong>Branch:</strong> {employee.branch_name}
                            </Typography>
                          )}
                        </Box>

                        {employee.email && (
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mt: 0.7, color: textprimary }}
                          >
                            {employee.email}
                          </Typography>
                        )}
                      </Box>

                      {selected && (
                        <Chip
                          size="small"
                          label="Selected"
                          sx={{
                            fontWeight: 700,
                            color: theme.palette.primary.main,
                            backgroundColor: isDark
                              ? `${theme.palette.primary.main}22`
                              : `${theme.palette.primary.main}12`,
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </CustomTableScrollbar>
        </StyledDialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseEmployeeModal}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              color: textPrimary,
              borderColor: theme.palette.divider,
            }}
           data-app-cancel="true">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          APPROVE / REJECT / CANCEL CONFIRMATION
      =================================================== */}

      <Dialog
        open={actionModalOpen}
        onClose={handleCloseActionConfirmation}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
            ...dialogPaperStyles,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: textPrimary,
            fontWeight: 700,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          {actionType === "approve"
            ? "Approve Leave Application"
            : actionType === "reject"
            ? "Reject Leave Application"
            : "Cancel Leave Application"}
        </DialogTitle>

        <Divider />

        <StyledDialogContent
          sx={{
            py: 3,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography sx={{ color: textPrimary, fontWeight: 600 }}>
            {actionType === "approve"
              ? "Are you sure you want to approve this leave application? The employee's leave balance will be updated."
              : actionType === "reject"
              ? "Are you sure you want to reject this leave application?"
              : "Are you sure you want to cancel this leave application?"}
          </Typography>

          {/* Rejection reason — required by the reject
              endpoint body { rejection_reason } */}

          {actionType === "reject" && (
            <Box sx={{ mt: 2 }}>
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{
                  display: "block",
                  mb: 1,
                  letterSpacing: 0.5,
                  color: theme.palette.primary.main,
                }}
              >
                REJECTION REASON
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={3}
                value={actionReason}
                onChange={(event) => setActionReason(event.target.value)}
                placeholder="Enter reason for rejection..."
                sx={textFieldStyles}
              />
            </Box>
          )}

          {selectedApplication && (
            <Box
              sx={{
                mt: 2,
                p: 1.8,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                backgroundColor: theme.palette.action.hover,
              }}
            >
              <Typography sx={{ fontWeight: 700, color: textPrimary }}>
                {getApplicationEmployeeName(selectedApplication)}
              </Typography>

              <Typography variant="body2" sx={{ mt: 0.4, color: textprimary }}>
                {getApplicationEmployeeCode(selectedApplication)}
                {" • "}
                {getApplicationLeaveType(selectedApplication)}
              </Typography>

              <Typography variant="body2" sx={{ mt: 0.5, color: textprimary }}>
                {selectedApplication.from_date} → {selectedApplication.to_date}
              </Typography>
            </Box>
          )}
        </StyledDialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCloseActionConfirmation}
            disabled={actionSaving}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              color: textPrimary,
              borderColor: theme.palette.divider,
            }}
           data-app-cancel="true">
            Cancel
          </Button>

          <Button
            variant="contained"
            color={actionType === "approve" ? "success" : "error"}
            onClick={handleConfirmAction}
            disabled={actionSaving}
            startIcon={
              actionSaving ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
            sx={{
              textTransform: "none",
              borderRadius: 2,
              fontWeight: 600,
              minWidth: 110,
            }}
          >
            {actionSaving
              ? "Updating..."
              : actionType === "approve"
              ? "Approve"
              : actionType === "reject"
              ? "Reject"
              : "Cancel"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===================================================
          SNACKBAR
      =================================================== */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LeaveApplication;